<template>
  <div class="wrapper-input">
    <div class="text-field text-field_floating" :class="{ 'text-field_textarea': isTextarea }">
      <textarea
        v-if="isTextarea"
        :value="modelValue"
        class="text-field__input text-field__input--textarea"
        :class="{ 'text-field__input-error': error || (errors && errors.length > 0) }"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :rows="props.rows"
        autocomplete="off"
        @input="$emit('update:modelValue', $event.target.value)"
      />
      <input
        v-else
        :type="typeInput"
        :value="modelValue"
        class="text-field__input"
        :class="{ 'text-field__input-error': error || (errors && errors.length > 0) }"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        autocomplete="off"
        @input="$emit('update:modelValue', $event.target.value)"
      />

      <label class="text-field__label">{{ props.label }}</label>

      <template v-if="props.typeInput === 'password'">
        <UButton
          v-if="typeInput === 'password'"
          class="text-field__showText"
          icon="i-heroicons-eye-slash"
          size="sm"
          color="primary"
          square
          variant="ghost"
          type="password"
          @click.stop="switchVisibility"
        />
        <UButton
          v-else
          class="text-field__showText"
          icon="i-heroicons-eye"
          size="sm"
          color="primary"
          square
          variant="ghost"
          type="password"
          @click.stop="switchVisibility"
        />
      </template>

      <div v-if="props.currency" class="currency">{{ props.currency }}</div>
    </div>

    <div v-if="(error || (errors && errors.length > 0)) && showError" class="wrapper-input__mas">
      <IconTheErrorMas />
      <span class="text-xs text-red-500">{{ errors && errors.length > 0 ? errors[0].$message : (typeof error === 'string' ? error : '') }}</span>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  // Плейсхолдер обязателен для floating-label трюка (:placeholder-shown),
  // но визуально прозрачен — поэтому по умолчанию пробел.
  placeholder: {
    type: String,
    default: ' ',
  },
  label: {
    type: String,
    default: '',
  },
  typeInput: {
    type: String,
    default: 'text',
  },
  // Количество строк для typeInput="textarea"
  rows: {
    type: [String, Number],
    default: 3,
  },
  error: {
    type: String,
    default: '',
  },
  errors: {
    type: Array,
    default: () => [],
  },
  showError: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  currency: {
    type: String,
    default: '',
  },
});
defineEmits(['update:modelValue']);

const isTextarea = computed(() => props.typeInput === 'textarea');

const typeInput = ref(props.typeInput);
const switchVisibility = () => {
  return (typeInput.value = typeInput.value === 'password' ? 'text' : 'password');
};
</script>

<style lang="scss" scoped>
.text-field {
  &__label {
    @apply block;
  }

  &__input {
    @apply block w-full h-14 py-2.5 px-5 text-black bg-white border border-sky-200 rounded-lg transition delay-150 duration-300 ease-in-out;
    @apply dark:bg-gray-900 dark:text-white dark:border-gray-600;

    &::placeholder {
      @apply text-sky-400 dark:text-gray-500;
    }

    &:focus {
      @apply border-sky-600 outline-none dark:focus:border-sky-600;
    }

    &-error {
      @apply border-red-500 focus:border-red-500;
    }
  }

  &__input--textarea {
    @apply h-auto min-h-[7rem] resize-y leading-relaxed;
  }

  &_floating {
    @apply relative;

    & .text-field__input {
      @apply h-14 px-5 py-4 text-base pr-12;

      &::placeholder {
        @apply text-transparent;
      }

      &:focus,
      &:not(:placeholder-shown) {
        @apply pt-7 pb-2.5;
      }

      &:focus ~ .text-field__label,
      &:not(:placeholder-shown) ~ .text-field__label {
        @apply opacity-60 scale-75 -translate-y-2.5 -translate-x-3.5;
      }
    }

    // Textarea выше инпута: перебиваем h-14 из правила floating-инпута
    // (специфичность 0,3,0 > 0,2,0), высоту задаёт rows + min-height.
    &.text-field_textarea .text-field__input--textarea {
      @apply h-auto min-h-[7rem] pr-5;
    }

    // У многострочного поля плавающий лейбл всегда сверху (не центрируем по высоте).
    &.text-field_textarea .text-field__input--textarea:focus,
    &.text-field_textarea .text-field__input--textarea:not(:placeholder-shown) {
      @apply pt-7 pb-2.5;
    }

    & .text-field__label {
      @apply text-sky-400 dark:text-gray-400 px-5 py-4 absolute top-0 left-0 h-full pointer-events-none border-transparent transition delay-150 duration-300 ease-in-out;
    }

    &.text-field_textarea .text-field__label {
      @apply h-auto;
    }

    & .text-field__showText {
      @apply absolute top-[50%] right-3 -translate-y-2/4;
    }
  }
}

.wrapper-input {
  &__mas {
    @apply flex items-center gap-2 mt-1.5;
  }
  .currency {
    @apply absolute right-2 top-4 text-gray-600 dark:text-gray-300;
  }
}
</style>
